(() => {
  if (window.magnezmaticDiscountTiersAdapter) return;

  const marker = 'magnezmatic-discount-tiers';
  const states = new WeakMap();
  const pendingSections = new Map();
  const optionIds = (value) => String(value || '').split(',').filter(Boolean).join(',');
  const urlPath = (value) => {
    try { return new URL(value, window.location.origin).pathname; } catch { return ''; }
  };
  const sectionId = (root) => root.dataset.originalSection || root.dataset.section;
  const components = (root) => Array.from(root.querySelectorAll(marker)).filter((node) => node.closest('product-info') === root);
  const invalidate = (root) => components(root).forEach((node) => { node.hidden = true; });
  const matches = (node, pending) => !pending || (
    urlPath(node.dataset.productUrl) === pending.url && optionIds(node.dataset.optionValues) === pending.options
  );

  function mount(root) {
    if (!root?.isConnected || !root.matches('product-info')) return;
    const nodes = components(root);
    if (!nodes.length) return;
    const pending = pendingSections.get(sectionId(root));
    const node = nodes[0];
    const existing = states.get(root);
    if (existing?.pending) { invalidate(root); return; }
    // A newly connected root can be a full-product swap, including a rejected stale response.
    if (pending && (!pending.ready || !matches(node, pending) || pending.signal?.aborted)) {
      invalidate(root);
      return;
    }
    if (pending) pendingSections.delete(sectionId(root));
    states.set(root, { pending: null, node });
    node.hidden = node.dataset.displayTiers !== 'true' || node.dataset.available !== 'true';
    nodes.slice(1).forEach((duplicate) => { duplicate.hidden = true; });
  }

  function selectionStart({ data }) {
    const root = data?.event?.target?.closest('product-info');
    if (!root?.isConnected || !components(root).length) return;
    invalidate(root);
    const pending = {
      url: urlPath(data.target?.dataset.productUrl || root.pendingRequestUrl || root.dataset.url),
      options: optionIds((data.selectedOptionValues || []).join(',')),
      ready: false,
    };
    states.set(root, { pending });
    pendingSections.set(sectionId(root), pending);
    // A replaced product-info subscribes after us. Dawn replaces its controller during
    // the same synchronous publish, so bind only after every selection subscriber ran.
    queueMicrotask(() => {
      if (states.get(root)?.pending !== pending || pendingSections.get(sectionId(root)) !== pending) return;
      pending.signal = root.abortController?.signal;
      pending.ready = Boolean(pending.signal);
    });
  }

  function resolved({ data }) {
    if (!data?.html || !data.variant?.available) return;
    document.querySelectorAll('product-info').forEach((root) => {
      const state = states.get(root);
      const pending = state?.pending;
      if (!root.isConnected || sectionId(root) !== data.sectionId || !pending?.ready || pending.signal?.aborted) return;
      const sources = Array.from(data.html.querySelectorAll(marker)).filter((node) => {
        const sourceRoot = node.closest('product-info');
        return sourceRoot && sectionId(sourceRoot) === data.sectionId;
      });
      if (sources.length !== 1) return;
      const source = sources[0];
      const current = components(root);
      const variantInput = root.querySelector('product-form input[name="id"]');
      if (current.length !== 1 || !matches(source, pending) ||
          source.dataset.productId !== current[0].dataset.productId ||
          source.dataset.variantId !== String(data.variant.id) ||
          variantInput?.value !== String(data.variant.id)) return;
      const replacement = source.cloneNode(true);
      current[0].replaceWith(replacement);
      pendingSections.delete(sectionId(root));
      states.set(root, { pending: null, node: replacement });
      mount(root);
    });
  }

  const mountAll = () => document.querySelectorAll('product-info').forEach(mount);
  // Capture before Dawn starts its request so pending guidance never survives the selection.
  document.addEventListener('change', (event) => {
    const root = event.target.closest('variant-selects')?.closest('product-info');
    if (root) {
      invalidate(root);
      states.set(root, { pending: { invalidated: true } });
    }
  }, true);
  document.addEventListener('product-info:loaded', (event) => mount(event.target));
  document.addEventListener('shopify:section:load', mountAll);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('product-info').forEach((root) => {
      invalidate(root);
      states.delete(root);
      pendingSections.delete(sectionId(root));
    });
  });
  window.addEventListener('pageshow', mountAll);
  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, selectionStart);
  subscribe(PUB_SUB_EVENTS.variantChange, resolved);
  window.magnezmaticDiscountTiersAdapter = Object.freeze({ version: 1 });
  if (!customElements.get(marker)) {
    customElements.define(marker, class extends HTMLElement {
      connectedCallback() { mount(this.closest('product-info')); }
    });
  }
  mountAll();
})();
